import React, { useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { XCircle, Search, CheckCircle2, AlertTriangle, Phone, Loader2, Ticket, Info } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const REASONS = [
  "Change of travel plans",
  "Health / medical emergency",
  "Weather / route disruption",
  "Duplicate booking",
  "Better fare found",
  "Other",
];

const mockBookings = [
  { id: "PMR12345678", kind: "Package", title: "Manali - Solang - Rohtang", amount: 3750, travelDate: "2026-08-15", status: "Confirmed" },
  { id: "PMRB87654321", kind: "Bus", title: "PMR Volvo Sleeper (Churu → Delhi)", amount: 899, travelDate: "2026-08-05", status: "Confirmed" },
  { id: "PMRC11223344", kind: "Car", title: "Toyota Innova (Churu → Jaipur)", amount: 2199, travelDate: "2026-08-02", status: "Confirmed" },
];

const CancelPage = () => {
  const { toast } = useToast();
  const [stage, setStage] = useState("lookup"); // lookup | details | confirm | success
  const [form, setForm] = useState({ bookingId: "", phone: "" });
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = (e) => {
    e.preventDefault();
    if (!form.bookingId || !form.phone) {
      toast({ title: "Missing details", description: "Please enter both Booking ID and Phone." });
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      // Try to find in mock, else generate a mock record
      const found =
        mockBookings.find(
          (b) => b.id.toLowerCase() === form.bookingId.trim().toLowerCase()
        ) || {
          id: form.bookingId.toUpperCase(),
          kind: "Booking",
          title: "Confirmed Trip",
          amount: 2499,
          travelDate: "2026-09-10",
          status: "Confirmed",
        };
      setBooking(found);
      setStage("details");
    }, 900);
  };

  const submit = () => {
    if (!reason) {
      toast({ title: "Please choose a reason", description: "Cancellation reason is required." });
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setStage("success");
    }, 1200);
  };

  const daysUntilTravel = booking
    ? Math.max(0, Math.ceil((new Date(booking.travelDate) - new Date()) / (1000 * 60 * 60 * 24)))
    : 0;

  const refundPct = daysUntilTravel >= 15 ? 100 : daysUntilTravel >= 7 ? 75 : daysUntilTravel >= 3 ? 50 : daysUntilTravel >= 1 ? 25 : 0;
  const refundAmount = booking ? Math.round((booking.amount * refundPct) / 100) : 0;

  const reset = () => {
    setStage("lookup");
    setForm({ bookingId: "", phone: "" });
    setReason("");
    setNotes("");
    setBooking(null);
  };

  return (
    <>
      <Header />
      <main>
        <section className="relative pt-40 pb-24 md:pt-48 md:pb-28 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1920&q=80"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 to-slate-900/50" />
          <div className="relative max-w-7xl mx-auto px-6 text-white">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white text-xs font-semibold tracking-wider uppercase">
              <XCircle className="w-3.5 h-3.5" /> Cancel Booking
            </div>
            <h1 className="mt-4 text-4xl md:text-6xl font-bold tracking-tight max-w-3xl" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Cancel your ticket in a few clicks
            </h1>
            <p className="mt-4 text-white/85 text-lg max-w-2xl">
              Plans changed? No worries — request a cancellation and see your refund estimate instantly.
            </p>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-slate-50">
          <div className="max-w-3xl mx-auto px-6">
            {stage === "lookup" && (
              <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-xl bg-teal-600 text-white flex items-center justify-center">
                    <Ticket className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">Find your booking</h2>
                    <p className="text-sm text-slate-500">Enter the Booking ID from your confirmation SMS/email</p>
                  </div>
                </div>
                <form onSubmit={lookup} className="space-y-4 mt-2">
                  <div>
                    <Label htmlFor="bid">Booking ID / PNR *</Label>
                    <Input
                      id="bid"
                      value={form.bookingId}
                      onChange={(e) => setForm({ ...form, bookingId: e.target.value })}
                      placeholder="e.g. PMR12345678 or PMRB87654321"
                    />
                  </div>
                  <div>
                    <Label htmlFor="bphone">Registered Phone *</Label>
                    <Input
                      id="bphone"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="+91 98765 43210"
                    />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-base">
                    {loading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Looking up...</>
                    ) : (
                      <><Search className="w-4 h-4 mr-2" /> Find Booking</>
                    )}
                  </Button>
                </form>

                <div className="mt-6 rounded-lg bg-teal-50 border border-teal-100 p-4 flex gap-3">
                  <Info className="w-5 h-5 text-teal-700 shrink-0 mt-0.5" />
                  <div className="text-sm text-slate-700">
                    Can't find your Booking ID?{" "}
                    <a href="tel:+919397363140" className="font-semibold text-teal-700 underline">Call +91 9397363140</a>{" "}
                    or check the confirmation SMS from PMR Travels.
                  </div>
                </div>
              </div>
            )}

            {stage === "details" && booking && (
              <div className="space-y-4">
                <div className="bg-white p-6 rounded-2xl border border-slate-200">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-xs uppercase tracking-wider text-teal-700 font-semibold">{booking.kind}</div>
                      <h3 className="mt-1 text-lg font-bold text-slate-900">{booking.title}</h3>
                      <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                        <div className="text-slate-500">Booking ID</div>
                        <div className="font-mono font-semibold">{booking.id}</div>
                        <div className="text-slate-500">Travel Date</div>
                        <div className="font-semibold">{booking.travelDate}</div>
                        <div className="text-slate-500">Amount Paid</div>
                        <div className="font-semibold">₹{booking.amount.toLocaleString("en-IN")}</div>
                        <div className="text-slate-500">Status</div>
                        <div className="font-semibold text-green-700">{booking.status}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-2xl border border-slate-200">
                  <h3 className="text-lg font-bold text-slate-900">Refund estimate</h3>
                  <div className="mt-3 rounded-lg bg-slate-50 border border-slate-100 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs text-slate-500">Days until travel</div>
                        <div className="text-lg font-bold text-slate-900">{daysUntilTravel} days</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-slate-500">Refund</div>
                        <div className="text-2xl font-bold text-teal-700">₹{refundAmount.toLocaleString("en-IN")}</div>
                        <div className="text-[11px] text-slate-500">{refundPct}% of paid amount</div>
                      </div>
                    </div>
                    <div className="mt-3 text-[11px] text-slate-500">
                      Based on our{" "}
                      <Link to="/refund-policy" className="text-teal-700 font-semibold underline">refund policy</Link>. Final refund may vary based on operator terms.
                    </div>
                  </div>

                  <div className="mt-5 space-y-4">
                    <div>
                      <Label>Reason for cancellation *</Label>
                      <Select value={reason} onValueChange={setReason}>
                        <SelectTrigger><SelectValue placeholder="Select a reason" /></SelectTrigger>
                        <SelectContent>
                          {REASONS.map((r) => (
                            <SelectItem key={r} value={r}>{r}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="notes">Additional notes (optional)</Label>
                      <Textarea id="notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any details we should know?" />
                    </div>
                  </div>

                  <div className="mt-5 rounded-lg bg-orange-50 border border-orange-100 p-4 flex gap-3">
                    <AlertTriangle className="w-5 h-5 text-orange-600 shrink-0 mt-0.5" />
                    <div className="text-sm text-slate-700">
                      Once submitted, cancellation cannot be undone. Refund is processed to the original payment method within 5-7 business days.
                    </div>
                  </div>

                  <div className="mt-5 flex gap-3">
                    <Button variant="outline" onClick={reset} className="flex-1">Back</Button>
                    <Button onClick={submit} disabled={loading} className="flex-1 bg-red-600 hover:bg-red-700">
                      {loading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                      ) : (
                        <><XCircle className="w-4 h-4 mr-2" /> Confirm Cancellation</>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {stage === "success" && booking && (
              <div className="bg-white p-8 rounded-2xl border border-slate-200 text-center">
                <div className="w-16 h-16 rounded-full bg-green-100 mx-auto flex items-center justify-center">
                  <CheckCircle2 className="w-9 h-9 text-green-600" />
                </div>
                <h2 className="mt-5 text-2xl font-bold text-slate-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  Cancellation Received
                </h2>
                <p className="mt-2 text-slate-600 max-w-md mx-auto">
                  We've cancelled booking <span className="font-mono font-semibold">{booking.id}</span>. Your refund of <span className="font-semibold text-teal-700">₹{refundAmount.toLocaleString("en-IN")}</span> will reflect in 5-7 business days.
                </p>
                <div className="mt-6 rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm text-left space-y-1 max-w-md mx-auto">
                  <div className="flex justify-between"><span className="text-slate-500">Cancellation Ref</span><span className="font-mono font-semibold">CX{Date.now().toString().slice(-8)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Booking</span><span className="font-semibold">{booking.title}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Reason</span><span className="font-semibold">{reason}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Refund Amount</span><span className="font-semibold text-green-700">₹{refundAmount.toLocaleString("en-IN")}</span></div>
                </div>
                <div className="mt-6 flex flex-wrap gap-3 justify-center">
                  <Button asChild variant="outline">
                    <Link to="/refund-policy">View Refund Policy</Link>
                  </Button>
                  <Button asChild className="bg-teal-600 hover:bg-teal-700">
                    <a href="tel:+919397363140"><Phone className="w-4 h-4 mr-2" /> Call Support</a>
                  </Button>
                </div>
                <p className="mt-4 text-[11px] text-orange-600 font-semibold">DEMO MODE — Cancellation is MOCKED (no real refund initiated).</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default CancelPage;
